import { z } from "zod";
import {
  treatmentStartDatePrecisionSchema,
  treatmentStartSourceSchema,
  UNDERHALL_INELIGIBLE_REASONS,
} from "./underhall-eligibility";

// TES-1073 (Ö-298) — the documented earlier treatment start the doctor
// registers on any patient, not only a transfer patient. Emelie 2026-10-02:
// the source is F-07, an already saved start date, or Kirill's own
// registration (e.g. after a meeting). It is what the "Ella Underhåll"
// twelve-month rule counts from (underhall-eligibility.ts).

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date_required");

/**
 * What the doctor sends. Month and year precision are normalised by the
 * server to the first day of the period, so a stored date never claims a
 * day it does not know. `estimateConfirmed` is the doctor's explicit
 * confirmation that an estimated date shows at least twelve full months;
 * it is refused for any other precision.
 */
export const recordTreatmentStartSchema = z
  .object({
    treatmentStartedAt: isoDate,
    datePrecision: treatmentStartDatePrecisionSchema,
    source: treatmentStartSourceSchema,
    estimateConfirmed: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.estimateConfirmed && v.datePrecision !== "estimated") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["estimateConfirmed"],
        message: "estimate_confirmation_not_applicable",
      });
    }
  });
export type RecordTreatmentStart = z.infer<typeof recordTreatmentStartSchema>;

export const treatmentStartRecordSchema = z.object({
  treatmentStartedAt: isoDate,
  datePrecision: treatmentStartDatePrecisionSchema,
  source: treatmentStartSourceSchema,
  recordedBy: z.string().nullable(),
  recordedAt: z.string(),
  // Who confirmed that an estimate shows twelve full months, and when. Null
  // for every other precision, and for an unconfirmed estimate.
  estimateConfirmedBy: z.string().nullable(),
  estimateConfirmedAt: z.string().nullable(),
});
export type TreatmentStartRecord = z.infer<typeof treatmentStartRecordSchema>;

/** Where a prefill came from, so the form can say so. */
export const TREATMENT_START_PREFILL_ORIGINS = [
  // The start the doctor verified when approving her prescription transfer.
  "transfer_approval",
  // Her own date in F-07.
  "f07",
] as const;

export const employeeTreatmentStartSchema = z.object({
  // The newest registration, or null when none has been made.
  record: treatmentStartRecordSchema.nullable(),
  // A suggestion for the form when there is no registration. Never counted
  // by itself unless it is the transfer approval's verified start.
  prefill: z
    .object({
      treatmentStartedAt: isoDate,
      datePrecision: treatmentStartDatePrecisionSchema.nullable(),
      source: treatmentStartSourceSchema.nullable(),
      origin: z.enum(TREATMENT_START_PREFILL_ORIGINS),
    })
    .nullable(),
  // The treatment half of the "Ella Underhåll" rule, as the server's hard
  // block reads it (Ö-297). `eligibleFrom` is the first day it is met.
  underhall: z.object({
    status: z.enum(["eligible", "not_eligible"]),
    reasons: z.array(z.enum(UNDERHALL_INELIGIBLE_REASONS)),
    eligibleFrom: isoDate.nullable(),
  }),
});
export type EmployeeTreatmentStart = z.infer<
  typeof employeeTreatmentStartSchema
>;
